import { useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import deepEqual from 'fast-deep-equal';
import { ResourceRequestAccessLevel } from '~/types';
import { useResourcePlanFilterBasedOnSearchCriteria } from '~/modules/resourcing/hooks';
import useProjectResourcingContext from '~/modules/resourcing/contexts/useProjectResourcingContext';
import { useSessionStorage } from '~/modules/common/hooks';

export const HIRING_STATUS_SUMMARY_COUNT_QUERY = gql`
  query hiringStatusSummaryCount(
    $filter: HiringStatusSummaryFilter
    $requestAccessLevel: ResourceRequestAccessLevel
  ) {
    hiringStatusSummaryCount(
      filter: $filter
      requestAccessLevel: $requestAccessLevel
    ) {
      noHiringStatusCount
      hiringRequestedCount
      hiringWithdrawnCount
    }
  }
`;

const useHiringStatusSummaryCount = ({
  requestAccessLevel = ResourceRequestAccessLevel.All,
  cacheVariables = {}
} = {}) => {
  const { searchCriteria } = useProjectResourcingContext();
  const { planFilter } = useResourcePlanFilterBasedOnSearchCriteria({
    searchCriteria
  });

  const variables = {
    hiringStatusSummaryCountQueryVariables: {
      requestAccessLevel,
      filter: planFilter
    },
    cacheVariables
  };

  const { storedValue, setValue } = useSessionStorage(
    'HIRING-STATUS-FILTER-QUERY-AND-CACHE-VARIABLES',
    {}
  );

  const shouldUpdateStorage = useMemo(() => {
    return !deepEqual(storedValue, variables);
  }, [storedValue, variables]);

  useEffect(() => {
    if (shouldUpdateStorage) {
      setValue(variables);
    }
  }, [variables, setValue, shouldUpdateStorage]);

  const { loading, data } = useQuery(HIRING_STATUS_SUMMARY_COUNT_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: variables.hiringStatusSummaryCountQueryVariables
  });

  return {
    loading,
    ...(data?.hiringStatusSummaryCount || {})
  };
};

export default useHiringStatusSummaryCount;
